import { ArrowRight } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export interface OpensInPanelProps {
  /** Glyph size in px. Default `11` — reads at chip scale without shouting. */
  size?: number;
  /** Extra classes (rarely needed — tone comes from `currentColor`). */
  className?: string;
}

/**
 * OpensInPanel — the shared "this opens in the work area on the right" cue.
 *
 * One glyph for every surface that routes the user rightwards: HITL
 * proposal chips, structured-result chips, generated-media previews.
 * The meaning is learned once and holds everywhere — which is the whole
 * point of it being a component instead of three local arrows.
 *
 * Visible AT REST (at half opacity), not hover-only: an affordance you
 * must discover by hovering is no affordance at all, and on touch there
 * is no hover to begin with. On hover it fills in and slides 2px along
 * its own direction — the motion says "that way", it is not decoration.
 * `prefers-reduced-motion` is honoured globally in `styles.css`.
 *
 * Deliberately NOT `ExternalLink`: that glyph promises a new tab / a
 * place outside the app, which is the opposite of what happens here.
 *
 * Inherits `currentColor`, so it takes the tone of whatever chip variant
 * hosts it (amber warning, emerald done, plain ink). Must live inside an
 * element carrying Tailwind's `group` class for the hover state to fire.
 * `aria-hidden`: it reinforces the host's own accessible label, which
 * already names the action ("Apri proposta: …"), and a second
 * announcement would only add noise.
 */
export const OpensInPanel: React.FC<OpensInPanelProps> = ({ size = 11, className }) => (
  <ArrowRight
    size={size}
    strokeWidth={1.75}
    aria-hidden
    className={cn(
      "shrink-0 opacity-50 transition-[opacity,transform] duration-fast",
      "group-hover:translate-x-0.5 group-hover:opacity-100",
      "group-focus-visible:translate-x-0.5 group-focus-visible:opacity-100",
      className,
    )}
  />
);
OpensInPanel.displayName = "OpensInPanel";
